import cookie from 'js-cookie';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import injectClickOutside from 'react-click-outside';
import styled, { withTheme } from 'styled-components';
import { Arrow as UnstyledArrow } from '@zocdoc-frontend-common/assets-icons';
import { Link } from '@zocdoc-frontend-common/components-link';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    DesktopPatientLoginAndSignupLink,
    DesktopPatientLoginLink,
    DesktopPatientSignupLink,
    DesktopProviderLoginLink,
    DropdownLinks,
    MyAccountLinksConversionIds,
} from '@zocdoc-frontend-common/utils-constants/HeaderLinks';
import {
    lazyImport,
    LazyComponent,
} from '@zocdoc-frontend-common/utils-lazyimportcomponent';
import { grey25 } from '@zocdoc/branding/colors';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
    Metrics,
    MetricsClickable,
} from '@zocdoc/frontend-metrics';
import { GA_ACTION_TEXT, TRACKING_ELEMENTS } from './constants';

const getLoginModal = lazyImport(
    () =>
        import(
            /* webpackChunkName: "loginmodal", webpackPrefetch: 40 */ '@zocdoc-frontend-common/components-log-in-modal'
        ).then((module) => ({ default: module.LoginModalWithMetric })),
    'LoginModal'
);

class SignInDropdownView extends React.PureComponent {
    static propTypes = {
        defaultOpen: PropTypes.bool,
        defaultShowLoginModal: PropTypes.bool,
        displayName: PropTypes.string,
        dropdownMenuDataTest: PropTypes.string,
        facebookLoginUrl: PropTypes.string,
        fakeImportLoginModal: PropTypes.object,
        googleLoginUrl: PropTypes.string,
        appleLoginUrl: PropTypes.string,
        onSignInDropdownClick: PropTypes.func,
        onSignInDropdownLinkClick: PropTypes.func,
        recordRememberMeAssignment: PropTypes.func,
        resetLoginModal: PropTypes.func,
        returnUrl: PropTypes.string,
        sendMetric: PropTypes.func,
        showRememberMe: PropTypes.bool,
        splitLoginAndSignupLink: PropTypes.bool,
        trackSignInLinkClickConversion: PropTypes.func,
        useLoginModal: PropTypes.bool,
    };
    static defaultProps = {
        defaultOpen: false,
        defaultShowLoginModal: false,
        dropdownMenuDataTest: 'signin-dropdown-bar',
        facebookLoginUrl: '',
        googleLoginUrl: '',
        appleLoginUrl: '',
        onSignInDropdownClick: noop,
        onSignInDropdownLinkClick: noop,
        recordRememberMeAssignment: noop,
        resetLoginModal: noop,
        returnUrl: '',
        sendMetric: noop,
        showRememberMe: false,
        splitLoginAndSignupLink: false,
        trackSignInLinkClickConversion: noop,
        useLoginModal: false,
    };
    state = {
        dropdownOpen: this.props.defaultOpen,
        showLoginModal: this.props.defaultShowLoginModal,
    };

    render() {
        const { displayName, dropdownMenuDataTest } = this.props;
        const { dropdownOpen } = this.state;

        const arrowDirection = dropdownOpen ? 'up' : 'down';

        return (
            <Metrics component="SignInDropdown">
                <DropdownContainer>
                    <MetricsClickable
                        action="Click Sign in Dropdown"
                        element="ClosedDropdown"
                    >
                        <DropdownMenuBar
                            data-test={dropdownMenuDataTest}
                            onClick={this.handleClickDropdownMenuBar}
                        >
                            <DropdownMenuHeader>
                                {displayName}
                            </DropdownMenuHeader>
                            <StyledArrow
                                direction={arrowDirection}
                                height={8}
                            />
                        </DropdownMenuBar>
                    </MetricsClickable>
                    {this.maybeRenderDropdownMenu()}
                    {this.maybeRenderLoginModal()}
                </DropdownContainer>
            </Metrics>
        );
    }

    maybeRenderLoginModal() {
        const {
            facebookLoginUrl,
            fakeImportLoginModal,
            googleLoginUrl,
            appleLoginUrl,
            recordRememberMeAssignment,
            returnUrl,
            showRememberMe,
        } = this.props;
        if (this.state.showLoginModal) {
            const LoginModal = fakeImportLoginModal || getLoginModal();
            return (
                <LazyComponent componentName="LoginModal">
                    <LoginModal
                        facebookLoginUrl={facebookLoginUrl}
                        googleLoginUrl={googleLoginUrl}
                        appleLoginUrl={appleLoginUrl}
                        onClickClose={this.handleClickCloseLoginModal}
                        recordRememberMeAssignment={recordRememberMeAssignment}
                        returnUrl={returnUrl}
                        showRememberMe={showRememberMe}
                        showSignUpLink
                    />
                </LazyComponent>
            );
        }
    }

    maybeRenderDropdownMenu() {
        if (this.state.dropdownOpen) {
            return (
                <DropdownMenuContainer>
                    {this.renderPatientLoginAndSignupLinks()}
                    {this.renderDropdownMenuItem(DesktopProviderLoginLink)}
                </DropdownMenuContainer>
            );
        }
    }

    renderPatientLoginAndSignupLinks() {
        const { sendMetric, splitLoginAndSignupLink } = this.props;
        if (splitLoginAndSignupLink) {
            sendMetric({
                action: GA_ACTION_TEXT.showPatientSplitLoginAndSignup,
                element: TRACKING_ELEMENTS.signInDropdown,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
            return this.renderPatientSplitLoginAndSignupLink();
        } else {
            sendMetric({
                action: GA_ACTION_TEXT.showPatientDefaultLoginAndSignup,
                element: TRACKING_ELEMENTS.signInDropdown,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
            return this.renderDropdownMenuItem(
                DesktopPatientLoginAndSignupLink
            );
        }
    }

    renderPatientSplitLoginAndSignupLink() {
        const loginHref = this.props.useLoginModal
            ? {}
            : {
                  href: DesktopPatientLoginAndSignupLink.url,
              };
        return (
            <DropdownMenuItem
                key={DesktopPatientLoginAndSignupLink.key}
                data-test={`signin-dropdown-${DesktopPatientLoginAndSignupLink.dataTest}`}
            >
                <DropdownKey>{`Patients`}</DropdownKey>

                <DropdownLinkContainer>
                    <MetricsClickable
                        action={DesktopPatientLoginLink.metrics.action}
                        element={DesktopPatientLoginLink.metrics.element}
                    >
                        <MarginSignInLink
                            {...loginHref}
                            data-test={`signin-dropdown-link-${DesktopPatientLoginLink.dataTest}`}
                            onClick={this.handleClickLoginLink}
                        >
                            {`Log in`}
                        </MarginSignInLink>
                    </MetricsClickable>
                    <MetricsClickable
                        action={DesktopPatientSignupLink.metrics.action}
                        element={DesktopPatientSignupLink.metrics.element}
                    >
                        <SignInLink
                            data-test={`signin-dropdown-link-${DesktopPatientSignupLink.dataTest}`}
                            href={DesktopPatientLoginAndSignupLink.url}
                            onClick={this.handleClickSigninLink}
                        >
                            {`Sign up`}
                        </SignInLink>
                    </MetricsClickable>
                </DropdownLinkContainer>
            </DropdownMenuItem>
        );
    }

    renderDropdownMenuItem(link) {
        return (
            <DropdownMenuItem
                key={link.dataTest}
                data-test={`signin-dropdown-${link.dataTest}`}
            >
                <DropdownKey>{link.key}</DropdownKey>

                <DropdownLinkContainer>
                    <MetricsClickable
                        action={link.metrics.action}
                        element={link.metrics.element}
                    >
                        <SignInLink
                            data-test={`signin-dropdown-link-${link.dataTest}`}
                            href={link.url}
                            onClick={this.handleClickSigninLink}
                        >
                            {link.message}
                        </SignInLink>
                    </MetricsClickable>
                </DropdownLinkContainer>
            </DropdownMenuItem>
        );
    }

    getConversionIdForLink = (url) => {
        if (url === DropdownLinks.providerSignin) {
            return MyAccountLinksConversionIds.provider;
        } else if (url === DropdownLinks.patientSignin) {
            return MyAccountLinksConversionIds.patient;
        }
        return null;
    };

    handleClickSigninLink = (event) => {
        const { onSignInDropdownLinkClick, trackSignInLinkClickConversion } =
            this.props;
        const { location } = window;
        onSignInDropdownLinkClick(event);
        trackSignInLinkClickConversion({
            conversionId: this.getConversionIdForLink(
                event.currentTarget.getAttribute('href')
            ),
            sessionId: cookie.get('bsid').replace(/_.*$/, '').replace('-', ''),
            url: `${location.protocol}//${location.host}${location.pathname}`,
            userAgent: navigator.userAgent,
        });
    };

    handleClickLoginLink = (event) => {
        const { resetLoginModal, useLoginModal } = this.props;
        if (useLoginModal) {
            resetLoginModal();
            this.setState({
                dropdownOpen: false,
                showLoginModal: true,
            });
        } else {
            this.handleClickSigninLink(event);
        }
    };

    handleClickOutside = () => {
        this.setState({ dropdownOpen: false });
    };

    handleClickDropdownMenuBar = (event) => {
        const { onSignInDropdownClick } = this.props;
        const dropdownToggled = !this.state.dropdownOpen;

        if (dropdownToggled && onSignInDropdownClick) {
            onSignInDropdownClick(event);
        }

        this.setState({ dropdownOpen: dropdownToggled });
    };

    handleClickCloseLoginModal = () => {
        this.setState({
            showLoginModal: false,
        });
    };
}

const DropdownContainer = styled.div`
    width: 100%;
    position: relative;
`;
const DropdownMenuBar = styled.div`
    display: flex;
    width: 100%;
    border-style: solid;
    cursor: pointer;
    align-items: center;
`;
const DropdownMenuHeader = styled(Link)`
    display: inline-block;
    font-size: 14px;
    color: ${(p) => p.theme.header.usernameDropdown.itemTextColor};
`;
const StyledArrow = styled(UnstyledArrow)`
    flex-grow: 0;
    flex-shrink: 0;
    margin-left: 4px;
    margin-right: 0px;
    pointer-events: none;
    transform: translateY(-0.5px);
    padding-top: 2px;
`;
const DropdownMenuContainer = styled.div`
    background-color: ${(p) => p.theme.header.usernameDropdown.itemBgColor};
    border: 1px solid ${grey25};
    min-width: 220px;
    margin-top: 12px;
    position: absolute;
    right: 0;
    z-index: 20;
`;
const DropdownMenuItem = styled.div`
    align-items: center;
    background-color: ${(p) => p.theme.header.usernameDropdown.itemBgColor};
    color: ${(p) => p.theme.header.usernameDropdown.itemTextColor};
    display: flex;
    height: 40px;
    margin: 0 16px 13px;
    padding-top: 13px;

    :not(:first-child) {
        border-top: 2px solid ${grey25};
    }

    *:nth-child(1) {
        flex: 1 0 40%;
    }

    *:nth-child(2) {
        flex: 1 0 60%;
    }
`;

const DropdownKey = styled.div`
    display: inline-block;
    font: 14px/24px ${FontFamily.SharpSansBold};
`;
const DropdownLinkContainer = styled.div`
    display: inline-block;
    font: 14px/29px ${FontFamily.SharpSansSemibold};
`;
const SignInLink = styled(Link)`
    color: ${(p) => p.theme.header.usernameDropdown.itemTextColor};
`;
const MarginSignInLink = styled(SignInLink)`
    margin-right: 16px;
`;

export const SignInDropdown = withTheme(injectClickOutside(SignInDropdownView));
